import {
    DEFAULT_ELLIPSE_RADIUS,
    DEFAULT_HEAT_ZONE_COLOR,
    DEFAULT_HEAT_ZONE_RADIUS,
    DEFAULT_NODE_RADIUS,
    createEmptyDocument,
    createLayer,
    createStrokeStyle,
} from "./factories";
import type {
    ConcentricRingsEffect,
    Connection,
    ConnectionObject,
    ConnectionObjectOrder,
    ConnectionObjectRotation,
    ConnectionPreset,
    ConnectionRenderKind,
    DiagramArc,
    DiagramDocument,
    DiagramEllipse,
    DiagramHeatZone,
    DiagramLayer,
    DiagramLine,
    DiagramNode,
    DiagramPattern,
    DiagramPolygon,
    DiagramRect,
    Effect,
    GroupOutlinePreset,
    NodeGroup,
    OutlineProperty,
    OutlineShape,
    PatternObject,
    PatternOrder,
    RadialEmitterEffect,
    StrokeStyle,
    Vec2,
} from "./types";
import {
    CONNECTION_OBJECT_ORDERS,
    CONNECTION_OBJECT_ROTATIONS,
    CONNECTION_RENDER_KINDS,
    OUTLINE_SHAPES,
    PATTERN_ORDERS,
    coerceFill,
    coerceStrokeStyleId,
    newId,
} from "./types";

// --- persistence / migration ----------------------------------------------

/** Pre-Effects node shape: inline radial-emitter decorations on the node. */
interface LegacyRadialProperty {
    kind?: string;
    rayCount?: number;
    innerRadius?: number;
    outerRadius?: number;
    presetId?: string | null;
}

/**
 * Backfill an outline property and migrate the legacy `box`/`octagon` shapes
 * into the unified `angular` shape (box = 2 bends, octagon = 4 bends).
 */
function coerceOutline(
    prop: OutlineProperty,
    knownPatternIds: ReadonlySet<string>,
    knownStyleIds: ReadonlySet<string>,
    knownOutlinePresetIds: ReadonlySet<string> = new Set(),
): OutlineProperty {
    const rawShape = prop.shape as string | undefined;
    let shape: OutlineShape = "hull";
    let bendCount = prop.bendCount ?? 4;
    if (rawShape === "box") {
        // Legacy box -> angular with 2 bends.
        shape = "angular";
        bendCount = 2;
    } else if (rawShape === "octagon") {
        // Legacy octagon -> angular with 4 bends.
        shape = "angular";
        bendCount = 4;
    } else if (OUTLINE_SHAPES.includes(rawShape as OutlineShape)) {
        shape = rawShape as OutlineShape;
    }
    // A stale preset link (preset deleted) falls back to the outline's own
    // fields, so drop the id rather than leave a dangling reference.
    const presetId =
        prop.presetId && knownOutlinePresetIds.has(prop.presetId)
            ? prop.presetId
            : null;
    return {
        ...prop,
        presetId,
        shape,
        bendCount,
        strokeStyleId: coerceStrokeStyleId(prop.strokeStyleId, knownStyleIds),
        cornerRadius: prop.cornerRadius ?? 0,
        cornerStyle: prop.cornerStyle ?? "round",
        fill: coerceFill(prop.fill, knownPatternIds),
    };
}

/** Coerce a persisted group-outline preset, backfilling fields like an outline. */
function coerceGroupOutlinePreset(
    raw: Partial<GroupOutlinePreset>,
    knownPatternIds: ReadonlySet<string>,
    knownStyleIds: ReadonlySet<string>,
    index: number,
): GroupOutlinePreset {
    const fields = coerceOutline(
        { ...(raw as OutlineProperty), kind: "outline" },
        knownPatternIds,
        knownStyleIds,
    );
    const { kind: _kind, presetId: _presetId, ...styleFields } = fields;
    return {
        ...styleFields,
        id: raw.id ?? newId("group-outline-preset"),
        name: raw.name ?? `Outline preset ${index + 1}`,
    };
}

/** Coerce one persisted pattern, backfilling tiling fields added over time. */
function coercePattern(
    raw: Partial<DiagramPattern> & { svg?: unknown },
): DiagramPattern | null {
    if (!raw || typeof raw !== "object") return null;
    // Migrate the legacy single-`svg` shape into the objects list.
    let objects: PatternObject[] = [];
    if (Array.isArray(raw.objects)) {
        objects = raw.objects
            .filter(
                (o): o is PatternObject =>
                    !!o &&
                    typeof o === "object" &&
                    typeof (o as PatternObject).svg === "string",
            )
            .map((o) => ({
                id: o.id ?? newId("patobj"),
                name: o.name ?? "Object",
                svg: o.svg,
            }));
    } else if (typeof raw.svg === "string") {
        objects = [
            { id: newId("patobj"), name: raw.name ?? "Object", svg: raw.svg },
        ];
    }
    if (objects.length === 0) return null;
    return {
        id: raw.id ?? newId("pattern"),
        name: raw.name ?? "Pattern",
        objects,
        order: PATTERN_ORDERS.includes(raw.order as PatternOrder)
            ? (raw.order as PatternOrder)
            : "sequence",
        tileSize: raw.tileSize ?? 24,
        spacingX: raw.spacingX ?? 0,
        spacingY: raw.spacingY ?? 0,
        rotationDeg: raw.rotationDeg ?? 0,
        backgroundColor:
            typeof raw.backgroundColor === "string"
                ? raw.backgroundColor
                : null,
    };
}

/** Coerce one persisted stroke style, backfilling fields added over time. */
function coerceStrokeStyle(raw: Partial<StrokeStyle>): StrokeStyle {
    const fallback = createStrokeStyle(0);
    return {
        id: raw.id ?? newId("strokestyle"),
        name: raw.name ?? fallback.name,
        color: typeof raw.color === "string" ? raw.color : fallback.color,
        strokeWidth: raw.strokeWidth ?? fallback.strokeWidth,
        dash: typeof raw.dash === "string" ? raw.dash : "",
        lineCount: raw.lineCount ?? 1,
        lineSpacing: raw.lineSpacing ?? 6,
        renderKind: CONNECTION_RENDER_KINDS.includes(
            raw.renderKind as ConnectionRenderKind,
        )
            ? (raw.renderKind as ConnectionRenderKind)
            : "stroke",
        objectSvgs: Array.isArray(raw.objectSvgs)
            ? raw.objectSvgs
                  .filter(
                      (o): o is ConnectionObject =>
                          !!o &&
                          typeof o === "object" &&
                          typeof (o as ConnectionObject).svg === "string",
                  )
                  .map((o) => ({
                      id: o.id ?? newId("connobj"),
                      name: o.name ?? "Object",
                      svg: o.svg,
                  }))
            : [],
        objectColor:
            typeof raw.objectColor === "string" ? raw.objectColor : undefined,
        objectSpacing: raw.objectSpacing ?? 24,
        objectScale: raw.objectScale ?? 16,
        objectOrder: CONNECTION_OBJECT_ORDERS.includes(
            raw.objectOrder as ConnectionObjectOrder,
        )
            ? (raw.objectOrder as ConnectionObjectOrder)
            : "sequence",
        objectRotation: CONNECTION_OBJECT_ROTATIONS.includes(
            raw.objectRotation as ConnectionObjectRotation,
        )
            ? (raw.objectRotation as ConnectionObjectRotation)
            : "align",
        objectAngleDeg: raw.objectAngleDeg ?? 0,
        heatReactive: (raw as Partial<StrokeStyle>).heatReactive ?? false,
    };
}

/**
 * Coerce one persisted effect, backfilling per-kind fields added over time.
 * Returns null for effects of an unknown kind so they're dropped on load.
 */
function coerceEffect(
    raw: Partial<Effect>,
    knownStyleIds: ReadonlySet<string>,
): Effect | null {
    const id = raw.id ?? newId("effect");
    const name = raw.name ?? "Effect";
    const strokeStyleId = coerceStrokeStyleId(
        (raw as Partial<RadialEmitterEffect>).strokeStyleId,
        knownStyleIds,
    );
    if (raw.kind === "concentric-rings") {
        const e = raw as Partial<ConcentricRingsEffect>;
        return {
            id,
            name,
            kind: "concentric-rings",
            ringCount: e.ringCount ?? 3,
            innerRadius: e.innerRadius ?? 18,
            spacing: e.spacing ?? 12,
            strokeStyleId,
        };
    }
    if (raw.kind === "radial-emitter") {
        const e = raw as Partial<RadialEmitterEffect>;
        return {
            id,
            name,
            kind: "radial-emitter",
            rayCount: e.rayCount ?? 8,
            innerRadius: e.innerRadius ?? 14,
            outerRadius: e.outerRadius ?? 40,
            strokeStyleId,
        };
    }
    return null;
}

/**
 * Coerce a raw (possibly stale) persisted document into a valid one. Backfills
 * fields added over time and migrates legacy inline node `properties` (radial
 * emitters) into document-level reusable `effects`.
 */
export function coerceDocument(raw: unknown): DiagramDocument {
    const base = createEmptyDocument();
    if (!raw || typeof raw !== "object") return base;
    const incoming = raw as Partial<DiagramDocument> & {
        nodes?: Array<
            Partial<DiagramNode> & { properties?: LegacyRadialProperty[] }
        >;
    };

    // Layers: coerce the persisted list, or synthesize a single default layer
    // for legacy documents (which had no layers — every element then lands on
    // it via the `coerceLayerId` fallback below). Always non-empty.
    const incomingLayers = (incoming as { layers?: unknown }).layers;
    const layers: DiagramLayer[] =
        Array.isArray(incomingLayers) && incomingLayers.length > 0
            ? incomingLayers
                  .filter((l): l is Partial<DiagramLayer> =>
                      Boolean(l && typeof l === "object"),
                  )
                  .map((l, i) => ({
                      id: l.id ?? newId("layer"),
                      name: l.name ?? `Layer ${i + 1}`,
                      visible: l.visible ?? true,
                      locked: l.locked ?? false,
                  }))
            : [createLayer(0)];
    const knownLayerIds = new Set(layers.map((l) => l.id));
    const defaultLayerId = layers[0]!.id;
    /** Keep a persisted layerId only when it names a known layer; else layer 0. */
    const coerceLayerId = (raw: unknown): string =>
        typeof raw === "string" && knownLayerIds.has(raw)
            ? raw
            : defaultLayerId;

    // Stroke styles first: effects, shapes, outlines, and presets all reference
    // them by id, so we need the valid id set before coercing those.
    const incomingStyles = (incoming as { strokeStyles?: unknown }).strokeStyles;
    const strokeStyles: StrokeStyle[] =
        Array.isArray(incomingStyles) && incomingStyles.length > 0
            ? incomingStyles
                  .filter((s): s is Partial<StrokeStyle> =>
                      Boolean(s && typeof s === "object"),
                  )
                  .map((s) => coerceStrokeStyle(s))
            : base.strokeStyles;
    const knownStyleIds = new Set(strokeStyles.map((s) => s.id));

    const effects: Effect[] = Array.isArray(incoming.effects)
        ? incoming.effects
              .map((e) =>
                  e && typeof e === "object"
                      ? coerceEffect(e, knownStyleIds)
                      : null,
              )
              .filter((e): e is Effect => e !== null)
        : [];

    const patterns: DiagramPattern[] = Array.isArray(incoming.patterns)
        ? incoming.patterns
              .map((p) =>
                  p && typeof p === "object" ? coercePattern(p) : null,
              )
              .filter((p): p is DiagramPattern => p !== null)
        : [];
    const knownPatternIds = new Set(patterns.map((p) => p.id));

    const groupOutlinePresets: GroupOutlinePreset[] = Array.isArray(
        incoming.groupOutlinePresets,
    )
        ? (incoming.groupOutlinePresets as Array<Partial<GroupOutlinePreset>>)
              .filter((p): p is Partial<GroupOutlinePreset> =>
                  Boolean(p && typeof p === "object"),
              )
              .map((p, i) =>
                  coerceGroupOutlinePreset(
                      p,
                      knownPatternIds,
                      knownStyleIds,
                      i,
                  ),
              )
        : [];
    const knownOutlinePresetIds = new Set(groupOutlinePresets.map((p) => p.id));

    type LegacyNode = Partial<DiagramNode> & {
        properties?: LegacyRadialProperty[];
    };
    const nodes: DiagramNode[] = Array.isArray(incoming.nodes)
        ? (incoming.nodes as LegacyNode[]).map((n) => {
              const effectIds = Array.isArray(n.effectIds)
                  ? [...n.effectIds]
                  : [];
              // Migrate legacy inline radial-emitter properties → effects.
              if (
                  effectIds.length === 0 &&
                  Array.isArray(n.properties) &&
                  n.properties.length > 0
              ) {
                  for (const prop of n.properties) {
                      if (!prop || prop.kind !== "radial-emitter") continue;
                      const eff: RadialEmitterEffect = {
                          id: newId("effect"),
                          name: `Effect ${effects.length + 1}`,
                          kind: "radial-emitter",
                          rayCount: prop.rayCount ?? 8,
                          innerRadius: prop.innerRadius ?? 14,
                          outerRadius: prop.outerRadius ?? 40,
                          // Legacy inline emitters referenced a connection
                          // preset; there's no stroke style to map to, so they
                          // load with the default stroke.
                          strokeStyleId: null,
                      };
                      effects.push(eff);
                      effectIds.push(eff.id);
                  }
              }
              return {
                  id: n.id ?? newId("node"),
                  label: n.label ?? "Node",
                  layerId: coerceLayerId(n.layerId),
                  position: n.position ?? { x: 0, y: 0 },
                  radius: n.radius ?? DEFAULT_NODE_RADIUS,
                  color: n.color ?? "#7ca7ff",
                  shape: n.shape ?? "circle",
                  visible: n.visible ?? true,
                  effectIds,
                  heatReactive: n.heatReactive ?? false,
                  spawn: n.spawn ?? null,
                  spawnedFrom: n.spawnedFrom ?? null,
              };
          })
        : base.nodes;

    // A connection always rides its endpoints' shared layer; for legacy docs
    // (no layerId) that resolves to the source node's coerced layer.
    const nodeLayerById = new Map(nodes.map((n) => [n.id, n.layerId]));

    return {
        layers,
        nodes,
        // Drop any legacy `mirrorOf` link so old mirror connections load as
        // plain, independent, fully-editable connections.
        connections: Array.isArray(incoming.connections)
            ? incoming.connections.map((c) => ({
                  id: c.id,
                  layerId:
                      typeof (c as Partial<Connection>).layerId === "string" &&
                      knownLayerIds.has((c as Connection).layerId)
                          ? (c as Connection).layerId
                          : nodeLayerById.get(c.sourceId) ?? defaultLayerId,
                  sourceId: c.sourceId,
                  targetId: c.targetId,
                  presetId: c.presetId,
                  // Preserve per-connection arc overrides when present; leave
                  // them undefined so the connection follows the preset.
                  ...(typeof c.arcFlip === "boolean"
                      ? { arcFlip: c.arcFlip }
                      : {}),
                  ...(typeof c.arcSweep === "number"
                      ? { arcSweep: c.arcSweep }
                      : {}),
              }))
            : base.connections,
        strokeStyles,
        presets:
            Array.isArray(incoming.presets) && incoming.presets.length > 0
                ? incoming.presets.map((p) => ({
                      id: p.id ?? newId("preset"),
                      name: p.name ?? "Connection",
                      // Reference a stroke style; fall back to the first one when
                      // missing so a connection always resolves to a style.
                      strokeStyleId:
                          coerceStrokeStyleId(
                              (p as Partial<ConnectionPreset>).strokeStyleId,
                              knownStyleIds,
                          ) ??
                          strokeStyles[0]?.id ??
                          "",
                      routing: p.routing ?? "straight",
                      bendCount: p.bendCount ?? 1,
                      cornerRadius: p.cornerRadius ?? 0,
                      cornerStyle: p.cornerStyle ?? "round",
                      arcFlip: p.arcFlip ?? false,
                      arcSpacedEnds: p.arcSpacedEnds ?? false,
                      arcEndsAlongChord: p.arcEndsAlongChord ?? false,
                      arcSweep: p.arcSweep ?? 0.5,
                      sineAmplitude: p.sineAmplitude ?? 16,
                      sineWavelength: p.sineWavelength ?? 48,
                      bridge: p.bridge ?? false,
                  }))
                : base.presets,
        effects,
        patterns,
        groups: Array.isArray(incoming.groups)
            ? incoming.groups.map((g) => ({
                  ...g,
                  layerId: coerceLayerId((g as Partial<NodeGroup>).layerId),
                  properties: (g.properties ?? []).map((prop) =>
                      prop.kind === "outline"
                          ? coerceOutline(
                                prop,
                                knownPatternIds,
                                knownStyleIds,
                                knownOutlinePresetIds,
                            )
                          : prop,
                  ),
              }))
            : base.groups,
        groupOutlinePresets,
        ellipses: Array.isArray(incoming.ellipses)
            ? (incoming.ellipses as Array<Partial<DiagramEllipse>>).map((e) => ({
                  id: e.id ?? newId("ellipse"),
                  label: e.label ?? "Ellipse",
                  layerId: coerceLayerId(e.layerId),
                  center: e.center ?? { x: 0, y: 0 },
                  rx: e.rx ?? DEFAULT_ELLIPSE_RADIUS,
                  ry: e.ry ?? DEFAULT_ELLIPSE_RADIUS,
                  color: e.color ?? "#7ca7ff",
                  strokeWidth: e.strokeWidth ?? 2,
                  dash: e.dash ?? "",
                  strokeStyleId: coerceStrokeStyleId(
                      e.strokeStyleId,
                      knownStyleIds,
                  ),
                  fill: coerceFill(e.fill, knownPatternIds),
                  visible: e.visible ?? true,
                  startAngle: e.startAngle ?? 0,
                  sweep: e.sweep ?? 1,
                  rotation: e.rotation ?? 0,
              }))
            : base.ellipses,
        lines: Array.isArray(incoming.lines)
            ? (incoming.lines as Array<Partial<DiagramLine>>).map((l) => ({
                  id: l.id ?? newId("line"),
                  label: l.label ?? "Line",
                  layerId: coerceLayerId(l.layerId),
                  start: l.start ?? { x: 0, y: 0 },
                  end: l.end ?? { x: 0, y: 0 },
                  color: l.color ?? "#7ca7ff",
                  strokeWidth: l.strokeWidth ?? 2,
                  dash: l.dash ?? "",
                  strokeStyleId: coerceStrokeStyleId(
                      l.strokeStyleId,
                      knownStyleIds,
                  ),
                  visible: l.visible ?? true,
              }))
            : base.lines,
        arcs: Array.isArray(incoming.arcs)
            ? (incoming.arcs as Array<Partial<DiagramArc>>).map((a) => ({
                  id: a.id ?? newId("arc"),
                  label: a.label ?? "Arc",
                  layerId: coerceLayerId(a.layerId),
                  start: a.start ?? { x: 0, y: 0 },
                  end: a.end ?? { x: 0, y: 0 },
                  color: a.color ?? "#7ca7ff",
                  strokeWidth: a.strokeWidth ?? 2,
                  dash: a.dash ?? "",
                  strokeStyleId: coerceStrokeStyleId(
                      a.strokeStyleId,
                      knownStyleIds,
                  ),
                  visible: a.visible ?? true,
                  flip: a.flip ?? false,
                  sweep: a.sweep ?? 0.5,
              }))
            : base.arcs,
        rects: Array.isArray(incoming.rects)
            ? (incoming.rects as Array<Partial<DiagramRect>>).map((r) => ({
                  id: r.id ?? newId("rect"),
                  label: r.label ?? "Rect",
                  layerId: coerceLayerId(r.layerId),
                  start: r.start ?? { x: 0, y: 0 },
                  end: r.end ?? { x: 0, y: 0 },
                  color: r.color ?? "#7ca7ff",
                  strokeWidth: r.strokeWidth ?? 2,
                  dash: r.dash ?? "",
                  strokeStyleId: coerceStrokeStyleId(
                      r.strokeStyleId,
                      knownStyleIds,
                  ),
                  visible: r.visible ?? true,
                  fill: coerceFill(r.fill, knownPatternIds),
                  cornerRadius: r.cornerRadius ?? 0,
                  cornerStyle: r.cornerStyle ?? "round",
                  rotation: r.rotation ?? 0,
              }))
            : base.rects,
        polygons: Array.isArray(incoming.polygons)
            ? (incoming.polygons as Array<Partial<DiagramPolygon>>).map((p) => ({
                  id: p.id ?? newId("polygon"),
                  label: p.label ?? "Polygon",
                  layerId: coerceLayerId(p.layerId),
                  points: Array.isArray(p.points)
                      ? p.points
                            .filter(
                                (pt): pt is Vec2 =>
                                    !!pt &&
                                    typeof pt === "object" &&
                                    typeof (pt as Vec2).x === "number" &&
                                    typeof (pt as Vec2).y === "number",
                            )
                            .map((pt) => ({ x: pt.x, y: pt.y }))
                      : [],
                  color: p.color ?? "#7ca7ff",
                  strokeWidth: p.strokeWidth ?? 2,
                  dash: p.dash ?? "",
                  strokeStyleId: coerceStrokeStyleId(
                      p.strokeStyleId,
                      knownStyleIds,
                  ),
                  fill: coerceFill(p.fill, knownPatternIds),
                  visible: p.visible ?? true,
                  cornerRadius: p.cornerRadius ?? 0,
                  cornerStyle: p.cornerStyle ?? "round",
              }))
            : base.polygons,
        heatZones: Array.isArray(incoming.heatZones)
            ? (incoming.heatZones as Array<Partial<DiagramHeatZone>>).map(
                  (z) => ({
                      id: z.id ?? newId("heatzone"),
                      label: z.label ?? "Heat zone",
                      layerId: coerceLayerId(z.layerId),
                      start: z.start ?? { x: 0, y: 0 },
                      end: z.end ?? { x: 0, y: 0 },
                      color: z.color ?? DEFAULT_HEAT_ZONE_COLOR,
                      radius: z.radius ?? DEFAULT_HEAT_ZONE_RADIUS,
                      visible: z.visible ?? true,
                  }),
              )
            : base.heatZones,
        symmetry: incoming.symmetry
            ? {
                  x: incoming.symmetry.x ?? false,
                  y: incoming.symmetry.y ?? false,
                  guideX: incoming.symmetry.guideX ?? false,
                  guideY: incoming.symmetry.guideY ?? false,
              }
            : base.symmetry,
    };
}
